import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getLeaderboard } from '../lib/api'
import type { LeaderboardResponse, LeaderboardEntry } from '../types'

const MEDALS = ['🥇', '🥈', '🥉']

function formatBabyDate(iso: string) {
  const date = new Date(iso.substring(0, 10) + 'T12:00:00').toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
  const time = iso.includes('T') ? ' à ' + iso.split('T')[1].substring(0, 5).replace(':', 'h') : ''
  return date + time
}

function ScoreBar({ value, max = 300 }: { value: number; max?: number }) {
  const pct = Math.round((value / max) * 100)
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-baby-400 rounded-full transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-bold text-baby-600 w-14 text-right">{value} / {max}</span>
    </div>
  )
}

const CHEATER_KEY = 'valentin thion'
const normStr = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z\s]/g, '').trim()
const isCheater = (name: string) => normStr(name).startsWith(normStr(CHEATER_KEY))

function CheaterCard({ entry }: { entry: LeaderboardEntry }) {
  const [open, setOpen] = useState(false)
  const dateFormatted = new Date(entry.arrival_date_guess.substring(0, 10) + 'T12:00:00').toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div className="card border-2 border-red-300 bg-red-50">
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-2xl w-8 flex-shrink-0">🚫</span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
              <p className="font-bold text-red-700 truncate line-through">{entry.participant_name}</p>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">Tricheur</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-stone-300 text-stone-700">Exclu du jeu</span>
            </div>
            <p className="text-xs text-red-300 line-through">✨ {entry.baby_name_guess} · ⚖️ {(entry.baby_weight_guess / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} kg · 📅 {dateFormatted}</p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="font-extrabold text-red-300 text-lg line-through">{entry.scores.total}</p>
          <p className="text-xs text-stone-400">pts</p>
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-red-200 space-y-2 animate-fade-in opacity-60">
          <div>
            <div className="flex justify-between text-xs text-red-400 mb-1">
              <span>✨ Prénom ({entry.scores.name === 100 ? 'exact !' : 'raté'})</span>
              <span className="line-through">{entry.scores.name} pts</span>
            </div>
            <ScoreBar value={entry.scores.name} max={100} />
          </div>
          <div>
            <div className="flex justify-between text-xs text-red-400 mb-1">
              <span>⚖️ Poids (écart : {entry.details.weight_diff_g}g)</span>
              <span className="line-through">{entry.scores.weight} pts</span>
            </div>
            <ScoreBar value={entry.scores.weight} max={100} />
          </div>
          <div>
            <div className="flex justify-between text-xs text-red-400 mb-1">
              <span>📅 Date (écart : {entry.details.hours_diff < 24
                ? `${entry.details.hours_diff}h`
                : `${Math.floor(entry.details.hours_diff / 24)}j${entry.details.hours_diff % 24 ? ` ${entry.details.hours_diff % 24}h` : ''}`
              })</span>
              <span className="line-through">{entry.scores.date} pts</span>
            </div>
            <ScoreBar value={entry.scores.date} max={100} />
          </div>
        </div>
      )}
    </div>
  )
}

function EntryCard({ entry, rank }: { entry: LeaderboardEntry; rank: number }) {
  const [open, setOpen] = useState(false)
  const medal = rank <= 3 ? MEDALS[rank - 1] : null
  const dateFormatted = new Date(entry.arrival_date_guess.substring(0, 10) + 'T12:00:00').toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div className={`card border-2 transition-colors ${rank === 1 ? 'border-amber-300 bg-amber-50' : 'border-stone-100'}`}>
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-2xl w-8 flex-shrink-0">{medal ?? <span className="text-stone-400 text-sm font-bold">#{rank}</span>}</span>
          <div className="min-w-0">
            <p className="font-bold text-stone-800 truncate">
              {entry.opt_in_leaderboard ? entry.participant_name : <span className="text-stone-400 italic">Anonyme</span>}
            </p>
            <p className="text-xs text-stone-400">✨ {entry.baby_name_guess} · ⚖️ {(entry.baby_weight_guess / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} kg · 📅 {dateFormatted}</p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="font-extrabold text-baby-600 text-lg">{entry.scores.total}</p>
          <p className="text-xs text-stone-400">pts</p>
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-stone-100 space-y-2 animate-fade-in">
          <div>
            <div className="flex justify-between text-xs text-stone-500 mb-1">
              <span>✨ Prénom ({entry.scores.name === 100 ? 'exact !' : 'raté'})</span>
              <span>{entry.scores.name} pts</span>
            </div>
            <ScoreBar value={entry.scores.name} max={100} />
          </div>
          <div>
            <div className="flex justify-between text-xs text-stone-500 mb-1">
              <span>⚖️ Poids (écart : {entry.details.weight_diff_g}g)</span>
              <span>{entry.scores.weight} pts</span>
            </div>
            <ScoreBar value={entry.scores.weight} max={100} />
          </div>
          <div>
            <div className="flex justify-between text-xs text-stone-500 mb-1">
              <span>📅 Date (écart : {entry.details.hours_diff < 24
                ? `${entry.details.hours_diff}h`
                : `${Math.floor(entry.details.hours_diff / 24)}j${entry.details.hours_diff % 24 ? ` ${entry.details.hours_diff % 24}h` : ''}`
              })</span>
              <span>{entry.scores.date} pts</span>
            </div>
            <ScoreBar value={entry.scores.date} max={100} />
          </div>
        </div>
      )}
    </div>
  )
}

export default function LeaderboardPage() {
  const [data, setData] = useState<LeaderboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getLeaderboard()
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-baby-50 to-amber-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8 animate-fade-in">
          <div className="text-6xl mb-3">🏆</div>
          <h1 className="text-3xl font-extrabold text-baby-700">Le Palmarès</h1>
        </div>

        {loading && (
          <div className="text-center py-16 text-stone-400">
            <svg className="animate-spin h-8 w-8 mx-auto mb-3" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            Chargement…
          </div>
        )}

        {error && (
          <div className="card border border-red-200 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && data && !data.published && (
          <div className="card text-center animate-slide-up">
            <div className="text-5xl mb-4">⏳</div>
            <h2 className="text-xl font-bold text-stone-700 mb-2">Pas encore disponible</h2>
            <p className="text-stone-500 text-sm">
              Le palmarès sera publié après la naissance du bébé ! Revenez nous voir en juin 2026.
            </p>
            <div className="mt-6">
              <Link to="/" className="btn-primary block">Participer au pronostic</Link>
            </div>
          </div>
        )}

        {!loading && !error && data?.published && data.baby && (
          <div className="space-y-5 animate-slide-up">

            {/* Baby announcement */}
            <div className="card bg-gradient-to-br from-baby-500 to-amber-500 text-white text-center border-0">
              <div className="text-5xl mb-3">👶</div>
              <h2 className="text-2xl font-extrabold mb-1">{data.baby.baby_name} est né{data.baby.baby_name.endsWith('e') ? 'e' : ''} !</h2>
              <div className="flex justify-center gap-6 mt-3 text-sm">
                <span>⚖️ {(data.baby.baby_weight / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 3 })} kg</span>
                <span>📅 {formatBabyDate(data.baby.arrival_date)}</span>
              </div>
            </div>

            {/* Scoring explanation */}
            <div className="bg-stone-50 rounded-xl px-4 py-3 text-xs text-stone-500 border border-stone-100">
              <strong>Comment ça marche ?</strong> Score sur 300 pts — Prénom exact (100 pts) · Poids proche (100 pts) · Date proche (100 pts). Cliquez sur une carte pour le détail.
            </div>

            {/* Leaderboard */}
            {(() => {
              const cheater = data.leaderboard.find(e => isCheater(e.participant_name))
              const clean = data.leaderboard.filter(e => !isCheater(e.participant_name))
              return (
                <div className="space-y-3">
                  {cheater && <CheaterCard entry={cheater} />}
                  {clean.map((entry, i) => (
                    <EntryCard key={entry.id} entry={entry} rank={i + 1} />
                  ))}
                  {clean.length === 0 && !cheater && (
                    <p className="text-center text-stone-400 py-8">Aucun pronostic enregistré.</p>
                  )}
                </div>
              )
            })()}

            <Link to="/" className="btn-secondary block text-center mt-4">
              ← Retour à l'accueil
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
